import {
	addBreadcrumb,
	captureException,
	withScope,
} from '@sentry/browser';

import {
	sanitizeBreadcrumbData,
	sanitizePayloadForSentry,
} from 'src/modules/sentry-sanitize';
import {
	SentryArea,
	SentryErrorSource,
	getSentryRouteContext,
} from 'src/modules/sentry-taxonomy';

export type SentryErrorPresentation = {
	name: string;
	context: string;
	fallbackMessage?: string;
};

const MAX_MESSAGE_LENGTH = 240;

const getRawErrorMessage = (
	error: unknown,
	fallbackMessage?: string
): string => {
	if (
		error instanceof Error &&
		typeof error.message === 'string' &&
		error.message.trim().length > 0
	) {
		return error.message.trim();
	}

	if (typeof error === 'object' && error !== null) {
		const candidate = error as {
			status?: number;
			response?: { status?: number; data?: { status?: number } };
			data?: { status?: number };
		};
		const status =
			candidate.response?.status ??
			candidate.status ??
			candidate.response?.data?.status ??
			candidate.data?.status;

		if (typeof status === 'number' && Number.isFinite(status)) {
			return `Request failed with status code ${status}`;
		}
	}

	return fallbackMessage ?? 'Falha inesperada';
};

export function formatSentryError(
	error: unknown,
	presentation: SentryErrorPresentation
): Error {
	const rawMessage = getRawErrorMessage(error, presentation.fallbackMessage);
	const prefix = `[${presentation.context}]`;
	const fullMessage = rawMessage.startsWith(prefix)
		? rawMessage
		: `${prefix} ${rawMessage}`;
	const truncatedMessage =
		fullMessage.length > MAX_MESSAGE_LENGTH
			? `${fullMessage.slice(0, MAX_MESSAGE_LENGTH - 3)}...`
			: fullMessage;

	const formattedError = new Error(truncatedMessage);
	formattedError.name = presentation.name;

	if (
		typeof error === 'object' &&
		error !== null &&
		'stack' in error &&
		typeof (error as { stack?: unknown }).stack === 'string'
	) {
		const originalStack = (error as { stack: string }).stack;
		const stackLines = originalStack.split('\n');
		const frames = stackLines.slice(1);
		formattedError.stack = `${presentation.name}: ${truncatedMessage}\n${frames.join(
			'\n'
		)}`;
	}

	return formattedError;
}

type CriticalBreadcrumbMetadata = {
	area: SentryArea;
	flow: string;
	step: string;
	action: string;
	data?: Record<string, unknown>;
	level?: 'info' | 'warning' | 'error';
};

type CriticalErrorMetadata = {
	area: SentryArea;
	flow: string;
	step: string;
	source: SentryErrorSource;
	context?: Record<string, unknown>;
	tags?: Record<string, string | undefined>;
	level?: 'warning' | 'error';
	fingerprint?: string[];
};

export const addCriticalBreadcrumb = ({
	area,
	flow,
	step,
	action,
	data,
	level = 'info',
}: CriticalBreadcrumbMetadata) => {
	addBreadcrumb({
		category: `critical.${area}`,
		level,
		message: action,
		data: sanitizeBreadcrumbData({
			area,
			flow,
			step,
			...data,
		}),
	});
};

export const captureCriticalError = (
	error: unknown,
	{
		area,
		flow,
		step,
		source,
		context,
		tags,
		level = 'error',
		fingerprint,
	}: CriticalErrorMetadata
) => {
	withScope(scope => {
		scope.setLevel(level);
		scope.setTag('area', area);
		scope.setTag('flow', flow);
		scope.setTag('step', step);
		scope.setTag('error_source', source);

		for (const [tag, value] of Object.entries(tags ?? {})) {
			if (value) {
				scope.setTag(tag, value);
			}
		}

		if (context) {
			scope.setContext(area, sanitizePayloadForSentry(context) as any);
		}

		if (fingerprint) {
			scope.setFingerprint(fingerprint);
		}

		captureException(error);
	});
};

export const shouldCaptureHandledError = (error: unknown) => {
	if (error === null || error === undefined) return false;
	if (typeof error !== 'object') return true;

	const apiError = error as {
		apiIssueCaptured?: boolean;
		config?: { sentryMeta?: { apiIssueCaptured?: boolean } };
		response?: {
			config?: { sentryMeta?: { apiIssueCaptured?: boolean } };
		};
	};

	if (apiError.apiIssueCaptured === true) {
		return false;
	}

	const sentryMeta =
		apiError.config?.sentryMeta ?? apiError.response?.config?.sentryMeta;

	return sentryMeta?.apiIssueCaptured !== true;
};

export const getAuthObservabilityContext = (pathname: string) => {
	const routeContext = getSentryRouteContext(pathname);
	const isCheckoutRoute = pathname.includes('/checkout');
	const isCreateEventRoute = pathname.includes('/me/events/');

	return {
		area: 'auth' as const,
		flow: routeContext.area === 'auth' ? routeContext.flow : 'login',
		step: routeContext.area === 'auth' ? routeContext.step : 'login',
		authSurface: isCheckoutRoute
			? 'checkout'
			: isCreateEventRoute
				? 'event_creation'
				: routeContext.area === 'auth'
					? 'page'
					: 'modal',
	};
};
